import type { PrinterStatusResponse, StoreInfo } from '@abcpay/shared'
import type { PrinterConfig } from '../types/receipt'

export interface ReceiptBranch {
  nid: string
  name: string
}

export function getDefaultReceiptStoreInfo(branchName: string): StoreInfo {
  const name = branchName.trim()
  const normalizedName = name.replace(/\s+/g, '').replace(/(?:지점|점)$/, '')
  const storeInfo = {
    name: name ? `ABC Currency Exchange (${name})` : 'ABC Currency Exchange',
    address: '',
    phone: '',
    businessNumber: '',
  }

  if (normalizedName === '충무로') {
    return {
      ...storeInfo,
      address: 'a108-1-ho, 3 Chungmu-ro, Jung-gu, Seoul',
      phone: '010-2972-2002',
      businessNumber: '811-85-02934',
    }
  }
  if (normalizedName === '광장' || normalizedName === '광장시장') {
    return { ...storeInfo, phone: '010-5628-2100', businessNumber: '694-85-02983' }
  }
  return storeInfo
}

export function getReceiptStoreInfo(branch: ReceiptBranch, config: PrinterConfig): StoreInfo {
  if (!branch.nid) throw new Error('거래 지점 정보가 없습니다. 거래 기록에서 다시 출력해주세요.')
  return {
    ...getDefaultReceiptStoreInfo(branch.name),
    ...config.receiptStoreInfoByBranch?.[branch.nid],
  }
}

// 하드웨어 설정만 저장하는 기존 화면에서도 지점별 영수증 설정을 보존한다.
export function mergePrinterSettings(current: PrinterConfig, next: PrinterConfig): PrinterConfig {
  return {
    ...current,
    ...next,
    receiptStoreInfoByBranch: next.receiptStoreInfoByBranch ?? current.receiptStoreInfoByBranch,
  }
}

export function createReceiptSettingsManager(storage: {
  load: () => Promise<PrinterConfig>
  save: (config: PrinterConfig) => Promise<PrinterStatusResponse>
}) {
  return {
    async load(branch: ReceiptBranch): Promise<StoreInfo> {
      return getReceiptStoreInfo(branch, await storage.load())
    },
    async save(branch: ReceiptBranch, info: StoreInfo): Promise<PrinterStatusResponse> {
      if (!branch.nid) throw new Error('지점을 선택해주세요.')
      if (!info.name.trim()) throw new Error('상호명을 입력해주세요.')
      const config = await storage.load()
      return storage.save({
        ...config,
        receiptStoreInfoByBranch: {
          ...config.receiptStoreInfoByBranch,
          [branch.nid]: {
            name: info.name.trim(),
            address: info.address.trim(),
            phone: info.phone.trim(),
            businessNumber: info.businessNumber?.trim() || '',
          },
        },
      })
    },
    async reset(branch: ReceiptBranch): Promise<PrinterStatusResponse> {
      if (!branch.nid) throw new Error('지점을 선택해주세요.')
      const config = await storage.load()
      const receiptStoreInfoByBranch = { ...config.receiptStoreInfoByBranch }
      delete receiptStoreInfoByBranch[branch.nid]
      return storage.save({ ...config, receiptStoreInfoByBranch })
    },
  }
}
